// Every time-based tunable, in milliseconds. Animation durations live in motion.ts.

/** Live stream (SSE). The backend sends `ping` every 15 s. */
export const STREAM = {
  /** silence this long means the stream is gone even if it looks open (proxies, mobile networks) */
  silenceMs: 40_000,
  /** how often the silence watchdog looks */
  watchdogMs: 10_000,
  /** reconnect backoff: base * 2^attempt, capped */
  reconnectBaseMs: 1_000,
  reconnectMaxMs: 30_000,
} as const;

/**
 * useStreamRefresh windows: a view refetches at most once per window after matching live messages
 * (never poll faster than a few seconds).
 */
export const REFRESH_MS = {
  default: 1_500,
  alertBadge: 500,
  overview: 2_500,
  /** the command center's map network (locations and readers): many requests, so seldom */
  overviewNetwork: 15_000,
  /** the overview map's aggregates and trucks (positions glide on their own in between) */
  overviewMap: 8_000,
  /** a role home (GET /home: cached 15 s by the server) */
  home: 5_000,
  shipments: 2_000,
  shipmentDetail: 1_200,
  cylinders: 2_500,
  cylinderDetail: 2_500,
  /** after a status change: the summary's counts follow within about a second (the stats worker), so they are asked then */
  cylinderSummarySettle: 1_200,
  locations: 4_000,
  locationDetail: 3_000,
  gateways: 3_000,
  gatewayDetail: 2_000,
  /** Pembaruan: the latest version's counts and the version list, after update messages (rows are patched at once) */
  otaSummary: 1_500,
  /** Pembaruan: the gateway list, after a message about many gateways (a rollout, a cancel) */
  otaList: 2_000,
  alerts: 1_500,
  operationsGateways: 2_000,
  operationsLocations: 3_000,
  locationShipments: 1_500,
  /** Operasional home: "Pembacaan terakhir" after a batch of reads at this location's reader */
  operationsReads: 700,
  scanSession: 2_000,
  presentTagsRegister: 700,
  presentTagsCheck: 1_000,
  /** a queue of orders or households after a stream notice */
  orders: 1_500,
  orderDetail: 1_000,
  households: 2_000,
} as const;

/** After a chunk-failure reload, failures within this window surface instead of reloading again (no loop). */
export const CHUNK_RELOAD_GUARD_MS = 10_000;

export const DEBOUNCE_MS = {
  /** list search boxes written back to ?q= */
  urlSearch: 350,
  /** operator serial-number search */
  serialSearch: 300,
  /** a search box inside a popup list (the people filter) */
  pickerSearch: 250,
  /** the location form asks for a code suggestion once the name rests this long */
  codeSuggestion: 400,
  /** the rollout drawer's summary, recounted after a choice changed */
  rolloutPreview: 300,
} as const;

/**
 * A tag taken away from the Reader may still be registered this long after the screen last saw it (the backend's
 * TAG_BINDING.goneGraceMs is 2 minutes from the Reader's last read; this stays inside it).
 */
export const TAG_GONE_GRACE_MS = 90_000;

/** Re-render intervals for relative times ("5 menit lalu") and truck positions. */
export const TICK_MS = {
  overview: 30_000,
  /** relative times in the live feed ("baru saja", "2 mnt") */
  feed: 60_000,
  /** the WIB clock in the header */
  clock: 1_000,
  register: 15_000,
  /**
   * Surat Jalan: progress and "Terlambat" follow the clock (no message says a truck became late), so the list and the
   * note re-read their answer this often while visible
   */
  shipmentTrip: 60_000,
  /** the overview's truck card: its progress and "Melewati perkiraan tiba" */
  tripCard: 15_000,
} as const;

export const TOAST_MS = {
  info: 4_500,
  error: 8_000,
  /** a critical alert stays long enough to be read and acted on (or until closed / "Lihat") */
  critical: 15_000,
} as const;

/** Operator mode: the stream may be down this long before the offline banner shows (no flash on a blip). */
export const OFFLINE_BANNER_DELAY_MS = 2_000;
/**
 * Reader pages: re-read on this interval while open. A reader that stops sending heartbeats sends no live message,
 * so "Tidak merespons" (computed by the server from the time) shows up only by asking again.
 */
export const READER_RECHECK_MS = 30_000;
/** Operasional home: "Pembacaan terakhir" is asked again this often besides the stream (a missed message, a reconnect). */
export const OPERATIONS_READS_POLL_MS = 20_000;
/** Overview: at most one arrival arc per route per window (a 24-cylinder receipt draws one arc, not 24). */
export const ARRIVAL_PULSE_THROTTLE_MS = 1_500;
/** Overview feed: rows that appear after the first render stay highlighted this long. */
export const FEED_FRESH_MS = 1_500;
/** The live indicator flashes at most once per window. */
export const LIVE_FLASH_THROTTLE_MS = 600;
/** Map: labels are re-measured once zoom/move animations and late layout have settled. */
export const MAP_DECLUTTER_SETTLE_MS = 350;
/** Browsers without requestIdleCallback prefetch route chunks this long after the area opened. */
export const PREFETCH_FALLBACK_DELAY_MS = 1_000;
/** Laptop view: the connection banner shows once the live stream has been down this long (or at once when offline). */
export const CONNECTION_LOST_DELAY_MS = 10_000;
/** Overview: one read ring per location per window (a reader sees a tag many times a second: 50 reads = 1 ring). */
export const READ_PULSE_THROTTLE_MS = 1_500;
/** Overview "Ikuti kejadian": the camera moves at most once per window, so the map never keeps jumping. */
export const FOLLOW_THROTTLE_MS = 6_000;
/** Overview 14-day trend: refetched on this interval, never per live event (the backend caches it for 60 s). */
export const TREND_REFRESH_MS = 60_000;
/** Laptop shell: the pointer resting (not moving) on the collapsed rail this long opens it over the page (hover-peek). */
export const RAIL_PEEK_DELAY_MS = 500;
/** ...and it closes this long after the pointer left (no flicker on a diagonal exit). */
export const RAIL_PEEK_CLOSE_MS = 150;
/** Map: the "3 terbaca" bubble next to a pin stays this long after the last read (a burst of reads is one bubble). */
export const READ_BUBBLE_MS = 2_000;
/** Overview: a Surat Jalan that just arrived (or departed) stays highlighted this long in the side panel. */
export const SHIPMENT_HIGHLIGHT_MS = 2_400;
/** Header: at most one toast for new critical alerts per window (the bell keeps the count). */
export const CRITICAL_TOAST_THROTTLE_MS = 5_000;
/** Overview: toasts are placed again this long after the page moved (rail pinned, panel collapsed). */
export const TOAST_PLACE_SETTLE_MS = 250;
/** Overview "Data terakhir …" (how fresh the screen is). Red is the stream being down (CONNECTION_LOST_DELAY_MS). */
export const FRESHNESS = {
  /** amber: readers that are online have sent nothing (no reads, no heartbeat) for this long */
  readerSilentMs: 10 * 60_000,
  /** the line counts up once a second */
  tickMs: 1_000,
} as const;
/** Optional tone for new critical alerts (off by default): at most one per window, two short falling notes. */
export const ALERT_TONE = {
  minGapMs: 30_000,
  noteMs: 140,
  notesHz: [880, 660],
  volume: 0.18,
  /** the envelope: start this far ahead, rise in attackSec, from and back to an inaudible gain */
  leadSec: 0.02,
  attackSec: 0.015,
  silentGain: 0.0001,
} as const;
/** Overview monitor mode: the cursor and the small exit bar hide after the mouse was still this long. */
export const MONITOR_IDLE_MS = 3_000;
/** Map trucks in transit: their estimated position is recomputed this often (one shared timer per map). */
export const TRUCK_TICK_MS = 1_000;
/** Overview "Putar ulang hari ini": today's Surat Jalan replayed on the map. */
export const REPLAY = {
  /** the replay clock advances this often (trucks and activity are recomputed) */
  tickMs: 500,
  /** playback speeds: 1 real second = 1 s, 10 s or 60 s of the day */
  speeds: [1, 10, 60] as const,
  defaultSpeed: 60,
  /** playback starts this long before the first step of the day (00:00 when there is none) */
  leadInMs: 10 * 60_000,
  /** arrivals crossed by a larger jump (dragging the slider) are not animated one by one */
  maxAnimatedStepMs: 15 * 60_000,
  /** the slider moves in steps of this (a whole minute would stop it short of "now") */
  sliderStepMs: 1_000,
  /** nobody touched the screen for this long: the replay closes and the live map is back (a 24 h screen) */
  autoCloseMs: 10 * 60_000,
} as const;

/** A finished photo upload stays in the upload list this long (with its check) before it leaves. */
export const PHOTO_UPLOAD_DONE_MS = 2_500;

/**
 * An upload whose bytes stop moving for this long counts as a lost connection (a stalled mobile link never
 * errors by itself); after the last byte, the server gets this long to answer (it decodes and resizes the photo).
 */
export const UPLOAD_STALL_MS = 30_000;
export const UPLOAD_ANSWER_MS = 90_000;

/** A load or a send still running after this long tells the person the connection is slow (it keeps going). */
export const SLOW_NETWORK_MS = 8_000;
/** Automatic retries of a Hub registration upload: the wait before each (ms), then a manual retry. */
export const UPLOAD_RETRY_MS = [2_000, 5_000, 15_000] as const;
/** The verifier's position: how long the device may take, and how old a fix may be. */
export const VISIT_GPS = { timeoutMs: 30_000, maxAgeMs: 0 } as const;
/** A submit that failed for a passing reason while offline is sent again this soon after the connection returns. */
export const RESEND_AFTER_ONLINE_MS = 1_500;
/** A JSON request without a whole answer after this long is given up as a lost connection (lib/api/client.ts › api). */
export const REQUEST_TIMEOUT_MS = 40_000;
/** A send that found no connection is tried again after these pauses (the last one repeats), and at once when it returns. */
export const RESEND_BACKOFF_MS = [3_000, 10_000, 30_000, 60_000] as const;

/** The SLA countdowns and "due soon" marks follow the clock this often. */
export const SLA_TICK_MS = 30_000;
/** The courier's device position at the house: one fix, fresh. */
export const DELIVERY_GPS = { timeoutMs: 20_000, maxAgeMs: 15_000 } as const;
/** The QR scanner: frames looked at per second (BarcodeDetector), and the pause after a read so one QR is read once. */
export const QR_SCAN = { intervalMs: 180, holdMs: 1_200 } as const;
/**
 * A USB UHF reader on the phone (OTG, keyboard mode) types a tag's EPC and Enter far faster than a person: keys at
 * most `maxGapMs` apart, at least `minChars` of them, make a read. Slower typing is manual entry.
 */
export const PHONE_READER = { maxGapMs: 45, minChars: 16 } as const;
/** The field outbox keeps an unsent item on the device at most this long. */
export const OUTBOX_KEEP_MS = 7 * 24 * 3_600_000;
